import React, { FC, useContext, useState } from 'react';
import { Asset, Footer, ConfirmationModal } from 'Shared/components';
import { currency } from 'Shared/utils';
import {
  Asset as Assets,
  Button,
  InfoFilledIcon,
  Modal,
  Tooltip,
} from '@clearcover/atlas';
import styles from './styles.module.css';
import { useLazyApi } from 'Shared/services/Api';
import { useHistory, useParams } from 'react-router-dom';
import { validateDraftEffectiveDate } from 'Policies/utils/validateDraftEffectiveDate';
import {
  isExternalAgentBackdate,
  isExternalAgentBackdatingError,
} from 'Policies/utils/isExternalAgentBackdate';
import { DatePickerModal } from 'Shared/components/DatePickerModal';
import { PolicyContext } from 'Policies/utils';
import { useUnderwritingErrors } from 'Shared/hooks/useUnderwritingErrors';
import { useAuth0 } from 'Auth';
import { WarningModal } from 'Policies/screens/PolicyDraftSummary/WarningModal';

type QuoteBarProps = {
  onSaveDraft: () => void;
  onUpdatePolicy: () => void;
  showQuoteBar: boolean;
  billingChangeSummary: {
    newFullTermValue: number;
    billingChangeValue: number;
    billingChangeMessage: string;
  };
  renewalDraftId?: string;
};

export const QuoteBar: FC<QuoteBarProps> = ({
  onSaveDraft,
  onUpdatePolicy,
  showQuoteBar,
  billingChangeSummary: {
    billingChangeMessage,
    billingChangeValue,
    newFullTermValue,
  },
  renewalDraftId,
}) => {
  const history = useHistory();
  const { id: policyId, draftId } = useParams<{
    id: string;
    draftId: string;
  }>();
  const {
    flags: { isFailingUnderwriting },
    details: { draftType, isRenewalDraft, effectiveDate, timeZone },
  } = useContext(PolicyContext);
  const { user } = useAuth0();
  // Fail closed: treat a missing user as external so the backdating guard
  // still applies when auth state is unavailable.
  const userIsInternal = user?.isInternalUser ?? false;
  const { underwritingErrors, setUnderwritingErrors } = useUnderwritingErrors();

  const [{ loading }, executeDateUpdate] = useLazyApi<{
    newDraftId: string;
  }>({
    url: `/policies/drafts/dateUpdate`,
    method: 'POST',
  });

  const [
    { loading: terminateDraftloading, error: terminateDraftError },
    executeTerminateDraft,
  ] = useLazyApi({
    url: `policies/${policyId}/drafts/${draftId}/terminate`,
    method: 'POST',
  });

  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [backdateModalOpen, setBackdateModalOpen] = useState(false);

  const premiumIncrease = billingChangeValue > 0;

  // Apply-time check drives its own modal state and deliberately does not
  // call setErrorMessage: the inline error disables "Review Changes", so
  // coupling it to this flow would leave the button stuck disabled after
  // the modal is dismissed.
  const handleReviewChanges = () => {
    if (
      isExternalAgentBackdate({
        effectiveDate,
        isInternalUser: userIsInternal,
        policyTimeZone: timeZone,
      })
    ) {
      setBackdateModalOpen(true);
      return;
    }
    onUpdatePolicy();
  };

  if (!showQuoteBar) return <></>;
  return (
    <Footer hasSecondaryMenu={false}>
      {loading ? (
        <div className={styles.loadingContainer}>
          <Asset variant="loading" />
        </div>
      ) : (
        <div className={styles.quoteBar} data-testid={'quote-bar'}>
          <DatePickerModal
            classNames={styles.datePicker}
            label={'Effective Date of Change'}
            disabled={draftType === 'renewal_quote'}
            onDateChange={(effectiveDate: string) => {
              setUnderwritingErrors(null);
              setErrorMessage(undefined);
              if (validateDraftEffectiveDate(effectiveDate, setErrorMessage)) {
                executeDateUpdate({
                  data: {
                    effectiveDate,
                    policyId,
                    draftId,
                  },
                })
                  .then(({ data: { newDraftId } }) => {
                    history.push(`/policies/${policyId}/draft/${newDraftId}`);
                  })
                  .catch((err) => {
                    // The backend independently rejects a backdated effective
                    // date for external agents. Surface the same modal as the
                    // client-side check rather than the inline error path.
                    if (isExternalAgentBackdatingError(err)) {
                      setBackdateModalOpen(true);
                      return;
                    }
                    setUnderwritingErrors(err);
                    setErrorMessage(err.message);
                  });
              }
            }}
            errorMessage={underwritingErrors[0] || errorMessage}
          />

          <div className={styles.rate}>
            <div className={styles.rateHeader}>New Full Term Premium</div>
            {isFailingUnderwriting ? (
              <div className={styles.underwritingErrMsg}>
                <Assets
                  icon={InfoFilledIcon}
                  height={20}
                  width={20}
                  description="Error Icon"
                />
                <span>Outstanding Rule Failures</span>
              </div>
            ) : (
              <>
                <div className={styles.additionalCost}>
                  <span>{currency(Math.abs(newFullTermValue)).format()}</span>
                  <span
                    className={
                      premiumIncrease ? styles.positive : styles.negative
                    }
                  >
                    {`${premiumIncrease ? '+' : '-'}${currency(
                      Math.abs(billingChangeValue)
                    ).format()}`}
                  </span>
                </div>
                {/* TODO: Update styling when this isn't here */}
                {draftType !== 'renewal_quote' && (
                  <p className={styles.rateFooter}>{billingChangeMessage}</p>
                )}
              </>
            )}
          </div>
          <div className={styles.buttonGroup}>
            {!isRenewalDraft && (
              <Modal
                variant="normal"
                toggle={(openModal) => (
                  <Button
                    data-testid="quote-bar-exit"
                    variant="danger"
                    onClick={openModal}
                  >
                    Delete Draft
                  </Button>
                )}
              >
                {(close) => (
                  <ConfirmationModal
                    onSubmit={() => {
                      executeTerminateDraft().then(() => {
                        if (draftType === 'renewal_quote') {
                          // Note: Although `renewalDraftId` is nullable, it will always have a value
                          // for renewal quotes, as they are built off of an existing renewal drafts.
                          // If the renewal draft is terminated or applied, the renewal quote will be
                          // discarded automatically.
                          history.push(
                            `/policies/${policyId}/draft/${renewalDraftId}`
                          );
                        } else {
                          history.push(`/policies/${policyId}`);
                        }
                      });
                    }}
                    close={close}
                    isSubmitting={terminateDraftloading}
                    error={terminateDraftError?.message}
                    headerText="Confirm discarding draft"
                    bodyText="Discarding draft will result in losing your current draft changes. Please confirm."
                  />
                )}
              </Modal>
            )}

            <Button
              data-testid="quote-bar-save"
              size="md"
              variant="secondary"
              disabled={!!errorMessage}
              onClick={() => onSaveDraft()}
            >
              Exit Draft
            </Button>
            {!isRenewalDraft && (
              <Tooltip
                position="top"
                tooltip={
                  isFailingUnderwriting ? 'Resolve outstanding errors' : ''
                }
              >
                <Button
                  data-testid="quote-bar-update"
                  size="md"
                  variant="primary"
                  disabled={!!errorMessage || isFailingUnderwriting}
                  onClick={handleReviewChanges}
                >
                  Review Changes
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
      )}
      {/* Rendered outside the loading/content ternary so it stays mounted
          while executeDateUpdate is in flight — the BE-rejection branch sets
          backdateModalOpen during the loading→false transition, and keeping
          the modal mounted avoids relying on React batching the two state
          updates into a single render. */}
      <WarningModal
        open={backdateModalOpen}
        handleClose={() => setBackdateModalOpen(false)}
        title="Please update the endorsement effective date to proceed"
        message="You may process this endorsement with an effective date today or after. Please reach out to customer support to process any endorsements with an effective date that has already occurred."
        buttons={[
          {
            testId: 'effective-date-backdate-modal-dismiss',
            variant: 'primary',
            size: 'md',
            isSubmitting: false,
            label: 'OK',
            onClick: () => {},
            closeOnClick: true,
          },
        ]}
      />
    </Footer>
  );
};
